import React, { useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';

import CLocalizedTextStringStatic from '../../../components/CLocalizedTextStringStatic';
import CPageModule from '../../../components/CPageModule';
import CPageModuleBrowsableTable from '../../../components/CPageModuleBrowsableTable';
import StatisticCard from '../../../components/StatisticCard';
import TableHyperlinkButton from '../../../components/TableHyperlinkButton';
import TimestampComponent from '../../../components/TimestampComponent';
import Page from '../../../components/layout/Page';
import { EAppType } from '../../../enums/AppType';
import { BreakpointsInPx } from '../../../enums/BreakpointsInPx';
import { ETableIds } from '../../../enums/ETableIds';
import { NexdosSecondaryColorEnum } from '../../../enums/NexdosSecondaryColorEnum';
import { PageRoutes } from '../../../enums/enums';
import { updateStateAttribute, updateStateResponseStatefulAttribute, useStateExtended } from '../../../helpers/helper';
import { useAppSelector } from '../../../hooks/hooks';
import { useUsageTrackingInfo } from '../../../hooks/useUsageTrackingInfo';
import { type CachedAPIResponse } from '../../../models/CachedAPIResponse';
import { type IOpsPerformancePositionsDTO } from '../../../models/IOpsPerformancePositionsDTO';
import { type IOpsPerformanceStatsDTO } from '../../../models/IOpsPerformanceStatsDTO';
import type { IRequestParams } from '../../../models/IRequestParams';
import type { IResponse } from '../../../models/IResponse';
import type { ISortingMeta } from '../../../models/ISortingMeta';
import { type TPageSize } from '../../../models/TPageSize';
import { SvPortfolioManagementServices } from '../../../services/SvPortfolioManagementServices';
import { SvUsageTrackingServices } from '../../../services/SvUsageTrackingServices';
import type { PortfolioManagementOutletContext } from '../PPortfolioManagementOutlet';

interface State {
  stats: IResponse<CachedAPIResponse<IOpsPerformanceStatsDTO>>;
  positions: IResponse<CachedAPIResponse<IOpsPerformancePositionsDTO>> & IRequestParams;
}

export default function PPortfolioManagementPerformance() {
  const { locale, dashboardOutletDimensions } = useAppSelector((state) => state.dashboard);
  const { factsheetId } = useOutletContext<PortfolioManagementOutletContext>();
  const factsheetID = factsheetId ?? '';
  const usageTrackingParams = useUsageTrackingInfo(EAppType.PortfolioManagementPerformance);

  const portfolioManagementPerformanceHeading = CLocalizedTextStringStatic('portfolioManagement.nav.performance');
  const portfolioManagementTitle = CLocalizedTextStringStatic('portfolioManagement.tab.title');
  const fundPerformanceLabel = CLocalizedTextStringStatic('portfolioManagement.performance.stats.fundPerformance');
  const swapPerformanceLabel = CLocalizedTextStringStatic('portfolioManagement.performance.stats.swapPerformance');
  const fundAUMLabel = CLocalizedTextStringStatic('portfolioManagement.performance.stats.fundAUM');

  const defaultSorting: ISortingMeta = { sortCol: 3, ascending: 'descending' };

  const [state, setState, getState] = useStateExtended<State>({
    stats: {
      state: {
        isLoading: true,
        isError: false
      }
    },
    positions: {
      state: {
        isLoading: true,
        isError: false
      },
      params: {
        sorting: defaultSorting,
        pageSize: 10
      }
    }
  });

  const fetchStats = async () => {
    await updateStateResponseStatefulAttribute<State, typeof state.stats.data>(
      {
        stats: (await getState()).stats
      },
      setState,
      async () =>
        await SvPortfolioManagementServices.getStatistics({
          factsheetID
        })
    );
  };

  const fetchPositions = async (page: number, sorting?: ISortingMeta, search?: string, pageSize: TPageSize = 10) => {
    await updateStateResponseStatefulAttribute<State, typeof state.positions.data>(
      {
        positions: (await getState()).positions
      },
      setState,
      async () =>
        await SvPortfolioManagementServices.getPositions({
          factsheetID,
          page,
          sorting: sorting ?? defaultSorting,
          search: search ?? '',
          pageSize
        })
    );

    updateStateAttribute<State>(
      {
        positions: {
          ...(await getState()).positions,
          params: {
            sorting,
            search,
            pageSize
          }
        }
      },
      setState
    );
  };

  const positionsTable = state.positions.data?.cacheData.positionsTable;

  useEffect(() => {
    (async () => {
      await Promise.all([SvUsageTrackingServices.createUsageTrackingRecord(usageTrackingParams)]);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      await Promise.all([fetchPositions(1, defaultSorting, ''), fetchStats()]);
    })();
  }, [locale, factsheetID]);

  return (
    <Page title={portfolioManagementPerformanceHeading}>
      <CPageModule apiRequestState={state.stats.state} showContent={!!state.stats.data}>
        <div
          className={'grid gap-4 grid-cols-2'}
          style={{
            gridTemplateColumns: `repeat(${
              dashboardOutletDimensions.w >= BreakpointsInPx.BREAKPOINT_3 ? 2 : 1
            }, minmax(0, 1fr))`
          }}
        >
          <div className={'flex flex-col gap-2'}>
            <div className={'grid gap-4 grid-cols-2'}>
              <StatisticCard
                label={fundPerformanceLabel}
                value={state.stats.data?.cacheData.fundPerformance}
                backgroundColor={NexdosSecondaryColorEnum.TEAL_PPT_6}
              />
              <StatisticCard
                label={swapPerformanceLabel}
                value={state.stats.data?.cacheData.swapPerformance}
                backgroundColor={NexdosSecondaryColorEnum.TEAL_PPT_7}
              />
            </div>
            <p className={'text-xs text-right pr-4'}>
              <TimestampComponent
                timestamp={
                  state.positions.data?.cacheData.maxTsSec && new Date(state.positions.data?.cacheData.maxTsSec * 1000)
                }
              />
            </p>
          </div>
          <div className={'flex flex-col gap-2'}>
            <StatisticCard
              label={fundAUMLabel}
              value={state.stats.data?.cacheData.fundAUM}
              backgroundColor={NexdosSecondaryColorEnum.TEAL_PPT_2}
            />
            <p className={'text-xs text-right pr-4'}>
              <TimestampComponent
                timestamp={
                  state.stats.data?.cacheData.fundAUMTsSec && new Date(state.stats.data?.cacheData.fundAUMTsSec * 1000)
                }
              />
            </p>
          </div>
        </div>
      </CPageModule>
      {(!!state.stats.data || !!state.positions.data) && (
        <CPageModule fullWidth>
          <CPageModule submodule>
            <h3>{portfolioManagementTitle}</h3>
          </CPageModule>

          <CPageModuleBrowsableTable
            id={ETableIds.PORTFOLIO_MANAGEMENT_PERFORMANCE_POSITIONS}
            state={state.positions.state}
            data={positionsTable?.results}
            tableMeta={positionsTable?.meta}
            pageSize={state.positions.params?.pageSize ?? 10}
            searchBar
            onUpdateTableMeta={async (nextMeta) => {
              await fetchPositions(nextMeta.page, nextMeta.sorting, nextMeta.search, nextMeta.pageSize ?? 10);
            }}
            columnConfig={[
              {
                span: 0,
                body: {
                  style: () => ({
                    textAlign: 'left'
                  })
                }
              },
              {
                span: 2.25,
                body: {
                  style: () => ({
                    textAlign: 'left'
                  }),
                  render: (context) => {
                    const results = state.positions.data?.cacheData.positionsTable.results;

                    if (!results) {
                      return String(context.value ?? '');
                    }

                    const stockUrl = results.stock_url?.[context.rowName];

                    if (!stockUrl) {
                      return String(context.value ?? '');
                    }

                    return (
                      <TableHyperlinkButton to={`/${PageRoutes.SINGLE_STOCK_OUTLET}/${stockUrl}`}>
                        {String(context.value)}
                      </TableHyperlinkButton>
                    );
                  }
                }
              },
              {
                span: 0.75,
                body: {
                  style: () => ({
                    textAlign: 'left'
                  })
                }
              },
              {
                span: 1.5,
                body: {
                  style: () => ({
                    textAlign: 'right'
                  })
                }
              },
              {
                span: 1.25,
                body: {
                  style: () => ({
                    textAlign: 'right'
                  })
                }
              },
              {
                span: 1.25,
                body: {
                  style: () => ({
                    textAlign: 'right'
                  })
                }
              },
              {
                span: 1,
                body: {
                  style: () => ({
                    textAlign: 'right'
                  })
                }
              },
              {
                span: 1.5,
                body: {
                  style: () => ({
                    textAlign: 'right'
                  })
                }
              },
              {
                span: 0
              }
            ]}
            rowConfig={{}}
          />
        </CPageModule>
      )}
    </Page>
  );
}
